import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";

// Define the shape of the theme object
export interface Theme {
  name: string;
  logo: string;
  logoWhite: string;
  logoBlack: string;
  logoSmall: string;
  palette: {
    primary: PaletteColor;
    secondary: PaletteColor;
    enrollment: PaletteColor;
    ghost: PaletteColor;
    disabled: PaletteColor;
    icon: PaletteColor;
  };
  typography: {
    fontFamily: string;
    fontSize: string;
    color: string;
  };
  copyright: string;
  shape: string | null;
  shapeInput: string;
  shapeLayout: string;
}

// Define the shape of the palette color object
interface PaletteColor {
  hover: string;
  main: string;
  outline: string;
  outlineHover: string;
  contrastText: string;
  hoverText: string;
  black?: string;
  text?: string;
}

// Define the initial state shape
interface ThemeState {
  status: "idle" | "loading" | "loaded" | "failed";
  errors: Record<string, unknown>;
  hasError: boolean;
  theme: Theme;
}

// Create async thunk for fetching theme
export const fetchTheme = createAsyncThunk<
  Theme,
  void,
  { rejectValue: string }
>("theme/fetchTheme", async (_, { rejectWithValue }) => {
  try {
    // Simulate a successful API call and return a valid Theme object
    const theme: Theme = {
      name: "MG",
      logo: "PATH_MG_LOGO_FULL",
      logoWhite: "PATH_MG_LOGO_WHITE",
      logoBlack: "PATH_MG_LOGO_BLACK",
      logoSmall: "PATH_MG_LOGO_SMALL",
      palette: {
        primary: {
          hover: "#000",
          main: "#132445",
          outline: "#999",
          outlineHover: "#999",
          contrastText: "#fff",
          hoverText: "#fff",
        },
        secondary: {
          hover: "#999",
          main: "#000",
          outline: "#999",
          outlineHover: "#999",
          contrastText: "#fff",
          hoverText: "#fff",
        },
        enrollment: {
          hover: "#999",
          main: "#6986B0",
          outline: "#999",
          outlineHover: "#999",
          contrastText: "#fff",
          hoverText: "#fff",
        },
        ghost: {
          hover: "#132445",
          main: "#666666",
          outline: "#000",
          outlineHover: "#000",
          contrastText: "#fff",
          hoverText: "#fff",
        },
        disabled: {
          hover: "#132445",
          main: "#cccccc",
          outline: "#000",
          outlineHover: "#999", // Add this property
          contrastText: "#fff",
          hoverText: "#fff", // Add this property
        },
        icon: {
          hover: "#000",
          main: "#000",
          outline: "transparent",
          outlineHover: "#999", // Add this property
          contrastText: "#fff", // Add this property
          hoverText: "#fff", // Add this property
        },
      },
      typography: {
        fontFamily: "Conv_Akk_Pro, Arial, sans-serif",
        fontSize: "0.875rem",
        color: "#212121",
      },
      copyright: "Mobility Global",
      shape: null,
      shapeInput: "round",
      shapeLayout: "round",
    };
    return theme;
  } catch (err) {
    // Handle the error and return a rejection value
    return rejectWithValue("Failed to fetch theme");
  }
});

/**
 * Default Theme
 */
function createTheme(
  primary: string,
  secondary: string,
  enrollment: string,
  disabled: string,
  ghost: string,
  outline: string,
  white: string,
  globalFont: string,
  black: string,
): Theme {
  return {
    name: "MG",
    logo: "PATH_MG_LOGO_FULL",
    logoWhite: "PATH_MG_LOGO_WHITE",
    logoBlack: "PATH_MG_LOGO_BLACK",
    logoSmall: "PATH_MG_LOGO_SMALL",
    palette: {
      primary: {
        hover: secondary,
        main: primary,
        outline: primary,
        outlineHover: secondary,
        contrastText: white,
        hoverText: white,
      },
      secondary: {
        hover: outline,
        main: secondary,
        outline: primary,
        outlineHover: secondary,
        text: black,
        contrastText: white,
        hoverText: white,
      },
      enrollment: {
        hover: secondary,
        main: enrollment,
        outline: outline,
        outlineHover: outline,
        contrastText: white,
        hoverText: white,
      },
      ghost: {
        hover: primary,
        main: ghost,
        outline: secondary,
        outlineHover: secondary,
        contrastText: disabled,
        hoverText: disabled,
      },
      disabled: {
        hover: primary,
        main: disabled,
        outline: secondary,
        outlineHover: secondary,
        contrastText: white,
        hoverText: white,
      },
      icon: {
        hover: secondary,
        main: secondary,
        outline: "transparent",
        outlineHover: secondary,
        contrastText: white,
        hoverText: white,
      },
    },
    typography: {
      fontFamily: "Conv_Akk_Pro, Arial, sans-serif",
      fontSize: "0.875rem",
      color: globalFont,
    },
    copyright: "Mobility Global",
    shape: null,
    shapeInput: "round",
    shapeLayout: "round",
  };
}

const initialTheme: Theme = createTheme(
  "#0064D2",
  "#132445",
  "#6986B0",
  "#cccccc",
  "#666666",
  "#999999",
  "#ffffff",
  "#212121",
  "#000",
);

const initialState: ThemeState = {
  status: "idle",
  errors: {},
  hasError: false,
  theme: initialTheme,
};

const themeSlice = createSlice({
  name: "theme",
  initialState,
  reducers: {
    resetTheme(state) {
      state.status = "idle";
      state.theme = initialTheme;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchTheme.pending, (state) => {
        state.status = "loading";
      })
      .addCase(fetchTheme.fulfilled, (state, action: PayloadAction<Theme>) => {
        state.status = "loaded";
        if (action.payload && Object.keys(action.payload).length !== 0) {
          state.theme = action.payload;
        } else {
          state.theme = initialTheme;
        }
      })
      .addCase(fetchTheme.rejected, (state) => {
        state.status = "failed";
        state.theme = initialTheme;
      });
  },
});

export const { resetTheme } = themeSlice.actions;

// Selectors
export const getTheme = (state: { theme: ThemeState }) => state.theme.theme;
export const getThemeName = (state: { theme: ThemeState }) =>
  state.theme.theme.name;
export const getCopyright = (state: { theme: ThemeState }) =>
  state.theme.theme.copyright;
export const getThemeLogoFull = (state: { theme: ThemeState }) =>
  state.theme.theme.logo;
export const getThemeLogoWhite = (state: { theme: ThemeState }) =>
  state.theme.theme.logoWhite;
export const getThemeLogoSmall = (state: { theme: ThemeState }) =>
  state.theme.theme.logoSmall;
export const getThemeIsAvailable = (state: { theme: ThemeState }) =>
  state.theme.status !== "loading";

export default themeSlice.reducer;
